import type { Area, Project, Tag, Task, TaskStatus } from '@shared/types';

async function http<T>(url: string, opts?: RequestInit): Promise<T> {
  const r = await fetch(url, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(opts?.headers ?? {}) },
    ...opts,
  });
  if (!r.ok) {
    let detail: any = undefined;
    try {
      detail = await r.json();
    } catch {}
    const err: any = new Error(detail?.error ?? `HTTP ${r.status}`);
    err.status = r.status;
    err.detail = detail;
    throw err;
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

export const api = {
  me: () => http<{ user: { id: string; email: string; name: string } | null }>('/api/me'),
  health: () => http<{ ok: boolean }>('/api/health'),

  // Auth
  registerBegin: (body: { email: string; name: string }) =>
    http<any>('/auth/register/begin', { method: 'POST', body: JSON.stringify(body) }),
  registerComplete: (att: any) =>
    http<{ ok: true; user: any }>('/auth/register/complete', { method: 'POST', body: JSON.stringify(att) }),
  loginBegin: (body: { email?: string }) =>
    http<any>('/auth/login/begin', { method: 'POST', body: JSON.stringify(body) }),
  loginComplete: (att: any) =>
    http<{ ok: true; user: any }>('/auth/login/complete', { method: 'POST', body: JSON.stringify(att) }),
  logout: () => http<{ ok: true }>('/auth/logout', { method: 'POST' }),

  // Tasks
  listTasks: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return http<Task[]>(`/api/tasks${q ? '?' + q : ''}`);
  },
  getTask: (id: string) => http<Task>(`/api/tasks/${id}`),
  createTask: (body: Partial<Task>) => http<Task>('/api/tasks', { method: 'POST', body: JSON.stringify(body) }),
  updateTask: (id: string, body: Partial<Task>) =>
    http<Task>(`/api/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  completeTask: (id: string) => http<{ ok: true }>(`/api/tasks/${id}/complete`, { method: 'POST' }),
  uncompleteTask: (id: string) => http<{ ok: true }>(`/api/tasks/${id}/uncomplete`, { method: 'POST' }),
  deleteTask: (id: string) => http<{ ok: true }>(`/api/tasks/${id}`, { method: 'DELETE' }),
  reorderTasks: (ids: string[]) =>
    http<{ ok: true }>('/api/tasks/reorder', { method: 'POST', body: JSON.stringify({ ids }) }),

  // Views
  view: (name: string, params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return http<any[]>(`/api/views/${name}${q ? '?' + q : ''}`);
  },
  search: (q: string) => http<any[]>(`/api/views/search?q=${encodeURIComponent(q)}`),
  stats: () => http<Record<string, number>>(`/api/views/stats`),

  // Projects/Areas/Tags
  listProjects: () => http<Project[]>('/api/projects'),
  createProject: (body: Partial<Project>) =>
    http<Project>('/api/projects', { method: 'POST', body: JSON.stringify(body) }),
  updateProject: (id: string, body: Partial<Project>) =>
    http<{ ok: true }>(`/api/projects/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteProject: (id: string) => http<{ ok: true }>(`/api/projects/${id}`, { method: 'DELETE' }),

  listAreas: () => http<Area[]>('/api/areas'),
  createArea: (body: Partial<Area>) => http<Area>('/api/areas', { method: 'POST', body: JSON.stringify(body) }),
  updateArea: (id: string, body: Partial<Area>) =>
    http<{ ok: true }>(`/api/areas/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteArea: (id: string) => http<{ ok: true }>(`/api/areas/${id}`, { method: 'DELETE' }),

  listTags: () => http<Tag[]>('/api/tags'),
  createTag: (body: Partial<Tag>) => http<Tag>('/api/tags', { method: 'POST', body: JSON.stringify(body) }),
  deleteTag: (id: string) => http<{ ok: true }>(`/api/tags/${id}`, { method: 'DELETE' }),

  // MCP tokens
  listMcpTokens: () => http<any[]>('/api/mcp-tokens'),
  createMcpToken: (name: string) =>
    http<{ id: string; name: string; token: string }>('/api/mcp-tokens', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  deleteMcpToken: (id: string) => http<{ ok: true }>(`/api/mcp-tokens/${id}`, { method: 'DELETE' }),
};

export type { Task, Project, Area, Tag, TaskStatus };
